import React, { Component } from 'react';
import _ from 'lodash';
import { addDays, subDays, startOfWeek } from 'date-fns';
import { Paper, Grid, Snackbar } from '@material-ui/core';

import WeekPicker from '../components/Schedule/WeekPicker';
import ScheduleLegend from '../components/Schedule/ScheduleLegend';
import ActionPanel from '../components/Schedule/ActionPanel';
import SideMenu from '../components/Schedule/SideMenu';
import PositionSchedule from '../components/Schedule/PositionSchedule';
import UnbookedFreelancerTable from '../components/Schedule/UnbookedFreelancerTable';
import SendConfirmationModal from '../components/Schedule/SendConfirmationModal';
import SendReleaseModal from '../components/Schedule/SendReleaseModal';
import DeleteBookingModal from '../components/Schedule/DeleteBookingModal';
import GenericConfirmActionModal from '../components/GenericConfirmActionModal';

import {
  fastTrack,
  fetchBookingScheduleData,
  fetchUnbookedFreelancers,
  generateConfirmation,
  releaseBookings,
  removeBookings,
} from '../helpers/externalRequests';
import { formatDateOnlyStringFromLocalTime, getLocalDateFromDateString, isBeforeToday } from '../helpers/helpers';
import BOOKING_STATUS from '../constants/employeeBookingStatus';

import i18n from '../i18n';

import '../assets/stylesheets/ScheduleView.css';

class ScheduleView extends Component {
  constructor(props) {
    super(props);
    this.state = {
      startOfSelectedWeek: startOfWeek(new Date(), {
        weekStartsOn: 1,
      }),
      positionSchedule: {},
      includeEmailSentEmployees: false,
      includeUnconfirmedEmployees: true,
      includeUnbookedFreelancers: false,
      includeConfirmedEmployees: false,
      includeCanceledEmployees: false,
      includeDeclinedEmployees: false,
      includeWeekend: false,
      selectAllDate: startOfWeek(new Date(), {
        weekStartsOn: 1,
      }),
      confirmation: null,
      showReleaseModal: false,
      showDeleteBookingsModal: false,
      unbookedFreelancers: [],
      modalData: {},
      snackbarMessage: '',
      snackOpen: false,
    };
  }

  componentDidMount() {
    const { startOfSelectedWeek, includeWeekend } = this.state;
    this.getScheduleData(startOfSelectedWeek, includeWeekend);
    this.getUnbookedFreelancersData(startOfSelectedWeek, includeWeekend);
  }

  getScheduleData = async (startOfSelectedWeek, includeWeekend) => {
    const daysInRange = includeWeekend ? 7 : 5;
    const startDate = formatDateOnlyStringFromLocalTime(startOfSelectedWeek);
    const endDate = formatDateOnlyStringFromLocalTime(
      addDays(new Date(startOfSelectedWeek), daysInRange - 1)
    );

    try {
      const response = await fetchBookingScheduleData(startDate, endDate);
      this.setState({
        startOfSelectedWeek,
        positionSchedule: response.data,
        selectAllDate: startOfSelectedWeek,
        showConfirmationModal: false,
        showReleaseModal: false,
        includeWeekend,
      });
    } catch {
      this.setState({
        startOfSelectedWeek,
        positionSchedule: {},
        selectAllDate: startOfSelectedWeek,
        showConfirmationModal: false,
        showReleaseModal: false,
        includeWeekend,
      });
    }
  };

  getUnbookedFreelancersData = async (startOfSelectedWeek, includeWeekend) => {
    const daysInRange = includeWeekend ? 7 : 5;
    const startDate = formatDateOnlyStringFromLocalTime(startOfSelectedWeek);
    const endDate = formatDateOnlyStringFromLocalTime(
      addDays(new Date(startOfSelectedWeek), daysInRange - 1)
    );

    try {
      const response = await fetchUnbookedFreelancers(startDate, endDate)
      this.setState({
        unbookedFreelancers: response.data,
      });
    } catch {
      this.setState({
        unbookedFreelancers: [], 
      });
    }
  };

  onWeekendClick = () => {
    const { startOfSelectedWeek, includeWeekend } = this.state;
    const toggledIncludeWeekend = !includeWeekend;

    let newStartOfWeekDate;
    if (toggledIncludeWeekend) {
      newStartOfWeekDate = subDays(startOfSelectedWeek, 1);
    } else {
      newStartOfWeekDate = addDays(startOfSelectedWeek, 1);
    }

    this.getScheduleData(newStartOfWeekDate, toggledIncludeWeekend);
    this.getUnbookedFreelancersData(newStartOfWeekDate, toggledIncludeWeekend);
  };

  onWeekChange = (dateSelected) => {
    const { includeWeekend } = this.state;
    const startOfWeekDate = startOfWeek(dateSelected, {
      weekStartsOn: includeWeekend ? 0 : 1,
    });
    this.getScheduleData(startOfWeekDate, includeWeekend);
    this.getUnbookedFreelancersData(startOfWeekDate, includeWeekend);
  };

  onSelectPreviousWeek = () => {
    const { startOfSelectedWeek, includeWeekend } = this.state;

    const newStartOfSelectedWeek = startOfWeek(
      subDays(startOfSelectedWeek, 7),
      { weekStartsOn: includeWeekend ? 0 : 1 }
    );
    this.getScheduleData(newStartOfSelectedWeek, includeWeekend);
    this.getUnbookedFreelancersData(newStartOfSelectedWeek, includeWeekend);
  };

  onSelectNextWeek = () => {
    const { startOfSelectedWeek, includeWeekend } = this.state;

    const newStartOfSelectedWeek = startOfWeek(
      addDays(startOfSelectedWeek, 7),
      { weekStartsOn: includeWeekend ? 0 : 1 }
    );
    this.getScheduleData(newStartOfSelectedWeek, includeWeekend);
    this.getUnbookedFreelancersData(newStartOfSelectedWeek, includeWeekend);
  };

  onChangeSelectAllDate = (selectedDate) => {
    this.onClickClearSelection();
    this.setState({ selectAllDate: selectedDate });
  };

  onClickSelectAll = () => {
    const { positionSchedule, selectAllDate } = this.state;
    const newPositionSchedule = JSON.parse(JSON.stringify(positionSchedule));

    const formattedSelectAllDate = formatDateOnlyStringFromLocalTime(
      selectAllDate
    );

    Object.keys(newPositionSchedule).forEach((positionId) => {
      const position = newPositionSchedule[positionId];
      Object.keys(position.scheduleData).forEach((locationId) => {
        const location = position.scheduleData[locationId];
        const dateBookings = location.scheduleBookings[formattedSelectAllDate];
        if (dateBookings) {
          Object.keys(dateBookings).forEach((employeeId) => {
            if (
              this.isEmployeeStatusFilterSelected(
                dateBookings[employeeId].status
              )
            )
              dateBookings[employeeId].isSelected = true;
          });
        }
      });
    });

    this.setState({ positionSchedule: newPositionSchedule });
  };

  onClickClearSelection = () => {
    const { positionSchedule } = this.state;
    const newPositionSchedule = JSON.parse(JSON.stringify(positionSchedule));

    Object.keys(newPositionSchedule).forEach((positionId) => {
      const position = newPositionSchedule[positionId];
      Object.keys(position.scheduleData).forEach((locationId) => {
        const location = position.scheduleData[locationId];
        Object.keys(location.scheduleBookings).forEach((date) => {
          const dateBookings = location.scheduleBookings[date];
          Object.keys(dateBookings).forEach((employeeId) => {
            dateBookings[employeeId].isSelected = false;
          });
        });
      });
    });

    this.setState({ positionSchedule: newPositionSchedule });
  };

  isEmployeeStatusFilterSelected = (selectedStatus) => {
    const {
      includeEmailSentEmployees,
      includeUnbookedFreelancers,
      includeConfirmedEmployees,
      includeCanceledEmployees,
      includeUnconfirmedEmployees,
      includeDeclinedEmployees,
    } = this.state;

    switch (selectedStatus) {
      case BOOKING_STATUS.EMAIL_SENT:
        return includeEmailSentEmployees;
      case BOOKING_STATUS.CANCELED:
        return includeCanceledEmployees;
      case BOOKING_STATUS.DECLINED:
        return includeDeclinedEmployees;
      case BOOKING_STATUS.EMAIL_CONFIRMED:
        return includeConfirmedEmployees;
      case BOOKING_STATUS.UNBOOKED_FREELANCER:
        return includeUnbookedFreelancers;
      case BOOKING_STATUS.DEFAULT:
        return includeUnconfirmedEmployees;
      default:
        return true;
    }
  };

  generateModalData = (date = new Date()) => {
    const { positionSchedule } = this.state;

    const modalData = {
      emailNotSentEmployees: [],
      fullTimeEmployees: [],
      freelanceConfirmedEmployees: [],
      fullTimeConfirmedEmployees: [],
      unconfirmedEmployees: [],
      confirmedEmployees: [],
      unbookedEmployees: [],
      canceledEmployees: [],
      declinedEmployees: [],
      expiredBookingEmployees: [],
    };

    Object.values(positionSchedule).forEach((position) => {
      Object.values(position.scheduleData).forEach((location) => {
        Object.entries(location.scheduleBookings).forEach(([bookedDate, dateBookings]) => {
          if (dateBookings) {
            Object.entries(dateBookings).forEach(([employeeId, employee]) => {
              if (employee.isSelected) {
                const employeeInfo = {
                  id: employeeId,
                  agents: employee.agents,
                  name: employee.employeeName,
                  email: employee.email,
                  bookedDate,
                  position: position.positionName,
                  bookingId: employee.bookingId,
                };
                const bookedDateAsDate = new Date(getLocalDateFromDateString(bookedDate));
                if (isBeforeToday(bookedDateAsDate, date)) {
                  modalData.expiredBookingEmployees.push(employeeInfo);
                } else {
                  switch (employee.status) {
                    case BOOKING_STATUS.EMAIL_SENT:
                      modalData.unconfirmedEmployees.push(employeeInfo);
                      break;
                    case BOOKING_STATUS.CANCELED:
                      modalData.canceledEmployees.push(employeeInfo);
                      break;
                    case BOOKING_STATUS.EMAIL_CONFIRMED:
                      // Distinguishing between full time confirmed and freelance confirmed employees for DeleteBookingModal
                      if (employee.isFullTime) {
                        modalData.fullTimeConfirmedEmployees.push(employeeInfo);
                      } 
                      else{modalData.freelanceConfirmedEmployees.push(employeeInfo);
                      }
                      modalData.confirmedEmployees.push(employeeInfo);
                      break;
                    case BOOKING_STATUS.DECLINED:
                      modalData.declinedEmployees.push(employeeInfo);
                      break;
                    case BOOKING_STATUS.UNBOOKED_FREELANCER:
                      modalData.unbookedEmployees.push(employeeInfo);
                      break;
                    case BOOKING_STATUS.DEFAULT:
                      if (employee.isFullTime) {
                        modalData.fullTimeEmployees.push(employeeInfo);
                      } else {
                        modalData.emailNotSentEmployees.push(employeeInfo);
                      }
                      break;
                    default:
                      break;
                  }
                }
              }
            });
          }
        });
      });
    });
    this.setState({ modalData });
  };

  onClickUnbookedFreelancer = () => {
    this.setState({
      snackOpen: true,
      snackbarMessage: i18n.t(
        'schedule:cannotSelectUnbookedFreelance'
      ),
    })
  }

  onClickSendConfirmationEmail = () => {
    this.generateModalData();
    this.setState({ showConfirmationModal: true });
  };

  onCloseConfirmationModal = () => {
    this.setState({ showConfirmationModal: false });
  };

  sendConfirmationEmails = async () => {
    const { modalData, startOfSelectedWeek, includeWeekend } = this.state;

    const selectedBookedEmployees = modalData.emailNotSentEmployees.map(
      (employee) => {
        const { id, bookedDate } = employee;
        return {
          id,
          bookedDate,
        };
      }
    );

    const employeesGroupedByDate = _.groupBy(
      selectedBookedEmployees,
      (employee) => employee.bookedDate
    );

    const emailPromises = Object.keys(employeesGroupedByDate).map((date) => {
      const selectedEmployeeIds = employeesGroupedByDate[date].map(
        (employee) => parseInt(employee.id, 10)
      );
      return generateConfirmation(date, selectedEmployeeIds)
    });
    try {
      await Promise.all(emailPromises);
      this.getScheduleData(startOfSelectedWeek, includeWeekend);
      this.setState({
        snackOpen: true,
        snackbarMessage: i18n.t(
          'schedule:successfullySentConfirmationEmails'
        ),
        showConfirmationModal: false,
      });      
    } catch {
      this.setState({
        snackOpen: true,
        snackbarMessage: i18n.t('schedule:errorSendingConfirmationEmails'),
        showConfirmationModal: false,
      });
    }
  };

  onClickSendReleaseEmail = () => {
    this.generateModalData();
    this.setState({ showReleaseModal: true });
  };

  onCloseReleaseModal = () => {
    this.setState({ showReleaseModal: false });
  };

  sendReleaseEmails = async () => {
    const { modalData, startOfSelectedWeek, includeWeekend } = this.state;

    const employeesToSendReleaseEmails = modalData.confirmedEmployees.concat(
      modalData.unconfirmedEmployees
    );

    const bookingIdsToRelease = employeesToSendReleaseEmails.map(
      (employee) => employee.bookingId
    );
    try {
      await releaseBookings(bookingIdsToRelease);
      this.getScheduleData(startOfSelectedWeek, includeWeekend);
      this.setState({
        snackOpen: true,
        snackbarMessage: i18n.t('schedule:successfullySentReleaseEmails'),
        showReleaseModal: false,
      });
    } catch {
      this.setState({
        snackOpen: true,
        snackbarMessage: i18n.t('schedule:errorSendingReleaseEmails'),
        showReleaseModal: false,
      });
    }
  };

  onEmployeeSelect = (positionId, locationId, date, employeeId) => {
    const { positionSchedule } = this.state;
    const newPositionSchedule = JSON.parse(JSON.stringify(positionSchedule));
    const selectedEmployee =
      newPositionSchedule[positionId].scheduleData[locationId].scheduleBookings[
        date
      ][employeeId];

    selectedEmployee.isSelected = !selectedEmployee.isSelected;

    this.setState({ positionSchedule: newPositionSchedule });
  };

  onClickDeleteBooking = () => {
    this.generateModalData();
    this.setState({ showDeleteBookingsModal: true });
  };

  onCloseDeleteBookingModal = () => {
    this.setState({ showDeleteBookingsModal: false });
  };

  onClickRemoveBookings = async () => {
    const bookingsToRemove = [];
    const { positionSchedule } = this.state;
    
    Object.keys(positionSchedule).forEach((positionId) => {
      const position = positionSchedule[positionId];
      Object.keys(position.scheduleData).forEach((locationId) => {
        const location = position.scheduleData[locationId];
        Object.keys(location.scheduleBookings).forEach((bookedDate) => {
          const dateBookings = location.scheduleBookings[bookedDate];
          if (dateBookings) {
            Object.keys(dateBookings).forEach((employeeId) => {
              if (
                dateBookings[employeeId].isSelected &&
                (dateBookings[employeeId].status === BOOKING_STATUS.DEFAULT ||
                  (dateBookings[employeeId].isFullTime &&
                    dateBookings[employeeId].status === BOOKING_STATUS.EMAIL_CONFIRMED))
              ) {
                const employeeInfo = {
                  bookingId: dateBookings[employeeId].bookingId,
                  employeeId,
                  availabilityType: dateBookings[employeeId].availabilityType,
                  day: bookedDate,
                };
                bookingsToRemove.push(employeeInfo);
              }
            });
          }
        });
      });
    });
    if (bookingsToRemove.length === 0) {
      this.setState({
        snackOpen: true,
        snackbarMessage: i18n.t(
          'schedule:errorInvalidSelection'
        ),
      });
      return;
    }
    try {
      await removeBookings(bookingsToRemove)
      this.setState({
        snackOpen: true,
        snackbarMessage: i18n.t(
          'schedule:successfullyDeletedBookings'
        ),
        showDeleteBookingsModal: false,
      });
      const { startOfSelectedWeek, includeWeekend } = this.state;
      this.getScheduleData(startOfSelectedWeek, includeWeekend);
    } catch {
      this.setState({
        snackOpen: true,
        snackbarMessage: i18n.t(
          'schedule:errorDeleteBookings'
        ),
        showDeleteBookingsModal: false,
      });
    }
  };

  onClickFastTrack = async () => {
    await this.confirm(i18n.t('booking:fastTrackConfirmation'))

    const bookingsToConfirm = [];
    const employeeNames = [];

    const { positionSchedule } = this.state;
    Object.keys(positionSchedule).forEach((positionId) => {
      const position = positionSchedule[positionId];
      Object.keys(position.scheduleData).forEach((locationId) => {
        const location = position.scheduleData[locationId];
        Object.keys(location.scheduleBookings).forEach((bookedDate) => {
          const dateBookings = location.scheduleBookings[bookedDate];
          Object.keys(dateBookings).forEach((employeeId) => {
            const booking = dateBookings[employeeId]
            if(booking.isSelected){
              bookingsToConfirm.push({
                id: booking.bookingId,
                confirmed: true
              });
              employeeNames.push(booking.employeeName);
            }
          });
        });
      });
    });
    fastTrack(bookingsToConfirm)
      .then(() => {
        this.setState({
          snackOpen: true,
          snackbarMessage: `${i18n.t(
            'schedule:successFastTrack'
          )} (${employeeNames.join(', ')})`,
        });
        const { startOfSelectedWeek, includeWeekend } = this.state;
        this.getScheduleData(startOfSelectedWeek, includeWeekend);
      });

  };

  displayUnbookedFreelancers = () => {
    const {
      startOfSelectedWeek,
      unbookedFreelancers,
      includeWeekend,
    } = this.state;

    return (
      <UnbookedFreelancerTable
        startOfSelectedWeek={startOfSelectedWeek}
        scheduleData={unbookedFreelancers}
        onEmployeeSelect={this.onClickUnbookedFreelancer}
        includeWeekend={includeWeekend}
      />);
  }

  displayPositionTables = () => {
    const {
      startOfSelectedWeek,
      positionSchedule,
      includeWeekend,
    } = this.state;

    const positionTables = Object.values(positionSchedule).map(
      (positionData) => {
        const { positionId, positionName, scheduleData } = positionData;
        return (
          <PositionSchedule
            key={positionId}
            positionId={positionId}
            positionName={positionName}
            startOfSelectedWeek={startOfSelectedWeek}
            scheduleData={Object.values(scheduleData)}
            onEmployeeSelect={this.onEmployeeSelect}
            includeWeekend={includeWeekend}
          />
        );
      }
    );

    return positionTables.length === 0 ? (
      <Paper className="blockContainer">
        <p>{i18n.t('schedule:noDataFound')}</p>
      </Paper>
    ) : (
      positionTables
    );
  };

  toggleSelectEmployeeFilter = (toggledStatus) => {
    this.setState((prevState) => {
      switch (toggledStatus) {
        case BOOKING_STATUS.EMAIL_SENT:
          return {
            includeEmailSentEmployees: !prevState.includeEmailSentEmployees,
          };
        case BOOKING_STATUS.CANCELED:
          return {
            includeCanceledEmployees: !prevState.includeCanceledEmployees,
          };
        case BOOKING_STATUS.DECLINED:
          return {
            includeDeclinedEmployees: !prevState.includeDeclinedEmployees,
          };
        case BOOKING_STATUS.EMAIL_CONFIRMED:
          return {
            includeConfirmedEmployees: !prevState.includeConfirmedEmployees,
          };
        case BOOKING_STATUS.DEFAULT:
          return {
            includeUnconfirmedEmployees: !prevState.includeUnconfirmedEmployees,
          };
        default:
          return prevState;
      }
    });
  };

  getPositionNames = () => {
    const { positionSchedule } = this.state;
    return Object.values(positionSchedule).map(
      (position) => position.positionName
    );
  };

  hideSnackbar = () => {
    this.setState({ snackOpen: false });
  };

  confirm = (msg) => {
    const promise = new Promise((resolve, reject) => {
      this.setState({confirmation: {
        msg,
        onClose: () => { 
          // eslint-disable-next-line prefer-promise-reject-errors
          reject('User Cancelled the confirmation modal') 
        },
        onConfirm: resolve
      }});
    });
    return promise.finally(() => {
      this.setState({confirmation: null});
    });
    
  }

  render() {
    const {
      startOfSelectedWeek,
      includeUnconfirmedEmployees,
      includeEmailSentEmployees,
      includeConfirmedEmployees,
      includeCanceledEmployees,
      includeDeclinedEmployees,
      selectAllDate,
      showConfirmationModal,
      modalData,
      showReleaseModal,
      showDeleteBookingsModal,
      includeWeekend,
      confirmation,
      snackbarMessage,
      snackOpen,
    } = this.state;

    return (
      <>
        <Grid container className="scheduleView">
          <Grid item xs={3} className="sectionContainer">
            <WeekPicker
              startOfSelectedWeek={startOfSelectedWeek}
              onChange={this.onWeekChange}
              onSelectPreviousWeek={this.onSelectPreviousWeek}
              onSelectNextWeek={this.onSelectNextWeek}
              includeWeekend={includeWeekend}
              onWeekendClick={this.onWeekendClick}
            />
            <ActionPanel
              onClickSelectAll={this.onClickSelectAll}
              includeUnconfirmedEmployees={includeUnconfirmedEmployees}
              includeEmailSentEmployees={includeEmailSentEmployees}
              includeConfirmedEmployees={includeConfirmedEmployees}
              includeCanceledEmployees={includeCanceledEmployees}
              includeDeclinedEmployees={includeDeclinedEmployees}
              toggleSelectEmployeeFilter={this.toggleSelectEmployeeFilter}
              onClickClearSelection={this.onClickClearSelection}
              onClickSendConfirmationEmail={this.onClickSendConfirmationEmail}
              onClickSendReleaseEmail={this.onClickSendReleaseEmail}
              onClickRemoveBookings={this.onClickDeleteBooking}
              selectAllDate={selectAllDate}
              onChangeSelectAllDate={this.onChangeSelectAllDate}
              onClickFastTrack={this.onClickFastTrack}
            />
            <SideMenu positionNames={this.getPositionNames()} />
            <ScheduleLegend />
          </Grid>
          <Grid item xs={9} className="sectionContainer positionsContainer">
            {this.displayUnbookedFreelancers()}
            {this.displayPositionTables()}
          </Grid>
        </Grid>

        {showDeleteBookingsModal && (
          <DeleteBookingModal
            showModal={showDeleteBookingsModal}
            onCloseDeleteBookingModal={this.onCloseDeleteBookingModal}
            onClickDeleteBookings={this.onClickRemoveBookings}
            deleteBookingModalData={modalData}
          />
        )}

        {showConfirmationModal && (
          <SendConfirmationModal
            showModal={showConfirmationModal}
            onCloseConfirmationModal={this.onCloseConfirmationModal}
            onClickSendEmails={this.sendConfirmationEmails}
            confirmationModalData={modalData}
          />
        )}

        {showReleaseModal && (
          <SendReleaseModal
            showModal={showReleaseModal}
            onCloseReleaseModal={this.onCloseReleaseModal}
            onClickSendReleaseEmails={this.sendReleaseEmails}
            releaseModalData={modalData}
          />
        )}

        {confirmation && (
          <GenericConfirmActionModal
            showModal
            onClose={confirmation.onClose}
            confirmMessage={confirmation.msg}
            onConfirm={confirmation.onConfirm}
          /> 
        )}

        <Snackbar
          message={snackbarMessage}
          open={snackOpen}
          onClose={this.hideSnackbar}
          autoHideDuration={2000}
        />
      </>
    );
  }
}

export default ScheduleView;
